/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines */
/* eslint-disable sonarjs/no-identical-functions */
import { Button } from 'antd';
import _ from 'lodash';
import React from 'react';
import { FormioFormProps } from 'src/components/FormioForm/FormioFormProps';
import { FormioCustomEvent } from 'src/core/Formio.types';

export interface ModalButton {
    label: string;
    type?: 'primary' | 'default' | 'dashed' | 'text' | 'link';
    danger?: boolean;
    onClick?: () => void;
    // For triggering Formio events
    formEvent?: {
        type: string;
        data?: any;
        component?: any;
    };
}

export interface ModalState {
    visible: boolean;
    title: string;
    content: React.ReactNode;
    footer?: React.ReactNode;
    width?: string | number;
    onOk?: () => void;
    onCancel?: () => void;
    // Enhanced button support
    buttons?: ModalButton[];
    // Form instance for triggering events
    formInstance?: any;
}

export interface ExtendedFormioFormProps extends FormioFormProps<any> {
    openModal?: (modalConfig: Partial<ModalState>) => void;
    closeModal?: () => void;
    formInstance?: any;
}

export const getCustomEventHandler = (type: string) => {
    return CUSTOM_EVENT_HANDLERS[type];
};

export type CustomEventHandler = (
    event: FormioCustomEvent<any>,
    formioFormProps: ExtendedFormioFormProps,
) => Promise<void>;

// Helper function to create modal footer with custom buttons
export const createModalFooter = (
    buttons: ModalButton[],
    closeModal: () => void,
    formInstance?: any,
): React.ReactNode => {
    if (!buttons || buttons.length === 0) {
        return null;
    }

    return (
        <div
            style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
            {buttons.map((button, index) => (
                <Button
                    key={index}
                    type={button.type || 'default'}
                    danger={button.danger}
                    onClick={() => {
                        // Handle custom onClick
                        if (button.onClick) {
                            button.onClick();
                        }

                        // Handle form event emission
                        if (button.formEvent && formInstance) {
                            formInstance?.formio?.emit?.(
                                button.formEvent.type,
                                {
                                    type: button.formEvent.type,
                                    data: button.formEvent.data || {},
                                    component: button.formEvent.component || {
                                        key: 'modalButton',
                                    },
                                },
                            );
                        }

                        // Close modal after button action (unless specified otherwise)
                        if (button.formEvent || button.onClick) {
                            closeModal();
                        }
                    }}
                >
                    {button.label}
                </Button>
            ))}
        </div>
    );
};

// Helper function to create common modal button patterns
export const createModalButtons = {
    // Confirmation modal with custom events
    confirm: (
        onConfirm: string | ModalButton['formEvent'],
        onCancel?: string | ModalButton['formEvent'],
    ): ModalButton[] => [
        {
            label: 'Cancel',
            type: 'default',
            formEvent:
                typeof onCancel === 'string' ? { type: onCancel } : onCancel,
        },
        {
            label: 'Confirm',
            type: 'primary',
            formEvent:
                typeof onConfirm === 'string' ? { type: onConfirm } : onConfirm,
        },
    ],

    // Action modal with custom action
    action: (
        actionLabel: string,
        actionEvent: string | ModalButton['formEvent'],
    ): ModalButton[] => [
        {
            label: 'Close',
            type: 'default',
        },
        {
            label: actionLabel,
            type: 'primary',
            formEvent:
                typeof actionEvent === 'string'
                    ? { type: actionEvent }
                    : actionEvent,
        },
    ],

    // Multiple custom buttons
    custom: (buttons: Omit<ModalButton, 'onClick'>[]): ModalButton[] =>
        buttons as ModalButton[],
};

const CUSTOM_EVENT_HANDLERS: Record<string, CustomEventHandler> = {
    submitWithoutData: async (event, { onSubmit }) => {
        if (onSubmit) {
            const data = _.pick(event.data, [event.component.key]);
            await onSubmit(data);
        }
    },

    // Enhanced modal event handlers
    openModal: (event, { openModal, closeModal, formInstance }) => {
        if (openModal) {
            const modalConfig: Partial<ModalState> = {
                title: event.data?.title || 'Modal',
                content: event.data?.content || 'Modal content',
                width: event.data?.width || 520,
                formInstance,
            };

            // Handle custom buttons
            if (event.data?.buttons) {
                modalConfig.buttons = event.data.buttons;
                modalConfig.footer = createModalFooter(
                    event.data.buttons,
                    closeModal!,
                    formInstance,
                );
            } else {
                modalConfig.footer = event.data?.footer;
            }

            // Handle custom onOk/onCancel with form events
            if (event.data?.onOk) {
                modalConfig.onOk = () => {
                    if (formInstance && event.data.onOk.formEvent) {
                        formInstance?.formio?.emit?.(
                            event.data.onOk.formEvent.type,
                            {
                                type: event.data.onOk.formEvent.type,
                                data: event.data.onOk.formEvent.data || {},
                                component: event.data.onOk.formEvent
                                    .component || {
                                    key: 'modalOk',
                                },
                            },
                        );
                    }
                    if (event.data.onOk.callback) {
                        event.data.onOk.callback();
                    }
                };
            }

            openModal(modalConfig);
        }

        return Promise.resolve();
    },

    openConfirmModal: (event, { openModal, closeModal, formInstance }) => {
        if (openModal) {
            const confirmEvent = event.data?.confirmEvent || 'confirmed';
            const cancelEvent = event.data?.cancelEvent || 'cancelled';

            const modalConfig: Partial<ModalState> = {
                title: event.data?.title || 'Confirm',
                content: event.data?.content || 'Are you sure?',
                width: event.data?.width || 400,
                formInstance,
                buttons: createModalButtons.confirm(
                    { type: confirmEvent, data: event.data?.confirmData },
                    { type: cancelEvent, data: event.data?.cancelData },
                ),
            };

            modalConfig.footer = createModalFooter(
                modalConfig.buttons!,
                closeModal!,
                formInstance,
            );

            openModal(modalConfig);
        }

        return Promise.resolve();
    },

    openInfoModal: (event, { openModal, closeModal, formInstance }) => {
        if (openModal) {
            const modalConfig: Partial<ModalState> = {
                title: event.data?.title || 'Information',
                content: event.data?.content || 'Information content',
                width: event.data?.width || 520,
                formInstance,
            };

            // Info modals can have custom action buttons
            if (event.data?.actionButton) {
                modalConfig.buttons = createModalButtons.action(
                    event.data.actionButton.label,
                    event.data.actionButton.event,
                );
                modalConfig.footer = createModalFooter(
                    modalConfig.buttons,
                    closeModal!,
                    formInstance,
                );
            } else {
                modalConfig.footer = null; // Default: only close button
            }

            openModal(modalConfig);
        }

        return Promise.resolve();
    },

    // Modal button event handlers - both close the modal
    modalConfirmed: (event, { closeModal }) => {
        // Handle confirm action
        if (closeModal) {
            closeModal();
        }

        return Promise.resolve();
    },

    modalCancelled: (event, { closeModal }) => {
        // Handle cancel action
        if (closeModal) {
            closeModal();
        }

        return Promise.resolve();
    },

    processAction: (event, formProps) => {
        // Add your custom logic here
        return Promise.resolve();
    },

    // Test button event from the modal
    testButtonClicked: (event, formProps) => {
        alert(
            `🎉 Modal button clicked! Message: ${
                event.data?.message || 'No message'
            }`,
        );

        return Promise.resolve();
    },

    // Close modal event
    closeModal: (event, { closeModal }) => {
        if (closeModal) {
            closeModal();
        }

        return Promise.resolve();
    },

    // Confirm action - sets number field to 200 and closes modal
    confirmAction: (event, { closeModal, formInstance }) => {
        // Set the number field to 200
        if (formInstance) {
            try {
                const numberComponent = formInstance.getComponent('number');
                if (numberComponent) {
                    numberComponent.setValue(200);
                }
            } catch (error) {
                console.error('Error setting number field:', error);
            }
        }

        // Close the modal
        if (closeModal) {
            closeModal();
        }

        return Promise.resolve();
    },

    // Reject action - sets number field to 100 and closes modal
    rejectAction: (event, { closeModal, formInstance }) => {
        // Set the number field to 100
        if (formInstance) {
            try {
                const numberComponent = formInstance.getComponent('number');
                if (numberComponent) {
                    numberComponent.setValue(100);
                }
            } catch (error) {
                console.error('Error setting number field:', error);
            }
        }

        // Close the modal
        if (closeModal) {
            closeModal();
        }

        return Promise.resolve();
    },
};
